import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";

export default function Accountability() {
  const [seats, setSeats] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ seat_title: "", person: "", roles: "" });

  const load = () => api.get("/seats").then((r) => setSeats(r.data));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.seat_title.trim()) return;
    await api.post("/seats", {
      seat_title: form.seat_title,
      person: form.person,
      roles: form.roles.split("\n").map((s) => s.trim()).filter(Boolean),
    });
    setOpen(false); setForm({ seat_title: "", person: "", roles: "" });
    toast.success("Seat added.");
    load();
  };

  const remove = async (id) => { if (confirm("Remove this seat?")) { await api.delete(`/seats/${id}`); load(); } };

  return (
    <div className="p-6 lg:p-8">
      <div className="flex items-start justify-between">
        <div>
          <div className="mono text-xs uppercase tracking-widest text-neutral-500">Module · Accountability</div>
          <h1 className="font-display text-4xl font-bold mt-2">Accountability Chart</h1>
          <p className="text-neutral-600 mt-2">The seats your business needs — regardless of who's in them.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#E54D2E] hover:bg-[#C93E22] text-white rounded-sm btn-lift" data-testid="seats-add-btn"><Plus className="h-4 w-4 mr-1" /> Add Seat</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>New Seat</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Seat title (e.g. Visionary, Integrator, Head of Sales)</Label><Input value={form.seat_title} onChange={(e)=>setForm({...form,seat_title:e.target.value})} className="rounded-sm" data-testid="seat-title-input" /></div>
              <div><Label>Person in the seat</Label><Input value={form.person} onChange={(e)=>setForm({...form,person:e.target.value})} className="rounded-sm" data-testid="seat-person-input" /></div>
              <div><Label>Roles (one per line — max 5)</Label><Textarea rows={5} value={form.roles} onChange={(e)=>setForm({...form,roles:e.target.value})} className="rounded-sm" data-testid="seat-roles-input" placeholder="Own the pipeline&#10;Hit revenue target&#10;Coach 3 AEs" /></div>
              <Button onClick={create} className="w-full bg-neutral-900 hover:bg-neutral-800 rounded-sm" data-testid="seat-create-submit">Add Seat</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {seats.length === 0 && <div className="col-span-full border border-neutral-200 bg-white p-12 text-center text-neutral-500">No seats yet. Start with your leadership team →</div>}
        {seats.map((s) => (
          <div key={s.id} className="border border-neutral-200 bg-white p-5 hover:border-neutral-900 transition-colors" data-testid={`seat-card-${s.id}`}>
            <div className="flex items-start justify-between">
              <div>
                <div className="font-display text-lg font-bold">{s.seat_title}</div>
                <div className="mono text-xs text-neutral-500 uppercase mt-1">{s.person || "OPEN SEAT"}</div>
              </div>
              <button onClick={() => remove(s.id)} className="text-neutral-400 hover:text-red-600" data-testid={`seat-delete-${s.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
            <ul className="mt-4 space-y-1.5 text-sm text-neutral-700">
              {(s.roles || []).map((r, i) => (
                <li key={i} className="flex gap-2"><span className="text-[#E54D2E]">·</span>{r}</li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}
